import React from 'react';
import { Box, Card, CardContent, CardMedia, CardActions, Button, Chip, Stack, Grid, Typography } from '@mui/material';
import { Book } from '@mui/icons-material';
import { getNotebookImage } from '../utils/notebookImage';

const cardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(10px) saturate(180%)',
  WebkitBackdropFilter: 'blur(10px) saturate(180%)',
  border: 'none',
  boxShadow: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: (theme) =>
      theme.palette.mode === 'dark'
        ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)'
        : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
    pointerEvents: 'none',
    zIndex: 1,
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: 6,
  },
};

const cardContentSx = {
  position: 'relative',
  zIndex: 1,
  minHeight: '140px',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(10px) saturate(180%)',
  WebkitBackdropFilter: 'blur(10px) saturate(180%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: (theme) =>
      theme.palette.mode === 'dark'
        ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)'
        : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
    pointerEvents: 'none',
    zIndex: 2,
  },
};

const chipSx = {
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(10px) saturate(180%)',
  WebkitBackdropFilter: 'blur(10px) saturate(180%)',
  border: (theme) =>
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.15)'
      : '1px solid rgba(255, 255, 255, 0.4)',
  boxShadow: (theme) =>
    theme.palette.mode === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.05)',
};

const cardActionsSx = {
  position: 'relative',
  zIndex: 1,
  p: 2,
  pt: 0,
  justifyContent: 'center',
  gap: 1,
  flexWrap: 'wrap',
  backgroundColor: (theme) =>
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(10px) saturate(180%)',
  WebkitBackdropFilter: 'blur(10px) saturate(180%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: (theme) =>
      theme.palette.mode === 'dark'
        ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)'
        : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
    pointerEvents: 'none',
    zIndex: 2,
  },
};

export default function NotebookCardGrid({
  notebookCategories,
  questions,
  prefetchQuestions,
  onViewQuestions,
  isTeacher,
}) {
  if (!notebookCategories?.length) return null;

  return (
    <Box sx={{ mt: 4, mb: 3 }}>
      <Typography component="h2" variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
        笔记本
      </Typography>
      <Grid container spacing={2}>
        {notebookCategories.map((notebookCat) => {
          const notebookQuestions = questions.filter(
            (q) => q.category === notebookCat.category && q.isNotebookExample
          );
          const notebook = notebookQuestions[0];
          const notebookIdForCard = notebook?.notebookId;
          const notebookTitle = notebookCat.category;
          const completedCount = notebookCat.completed || 0;
          const totalCount = notebookCat.count || 0;
          const completionRateForCard =
            totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

          return (
            <Grid item xs={12} sm={6} md={6} lg={3} key={notebookCat.category}>
              <Card
                onMouseEnter={() => prefetchQuestions(notebookIdForCard, notebookTitle)}
                sx={cardSx}
              >
                <CardMedia
                  sx={{ height: 100, position: 'relative', zIndex: 0 }}
                  image={getNotebookImage(notebookTitle)}
                  title={notebookTitle}
                />
                <CardContent sx={cardContentSx}>
                  <Typography
                    gutterBottom
                    variant="h5"
                    component="div"
                    fontWeight="bold"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.4,
                      minHeight: '3.5em',
                      mb: 1,
                    }}
                  >
                    {notebookTitle}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Book color="primary" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      {totalCount} 道题目
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip
                      label={`已完成 ${completedCount}/${totalCount}`}
                      size="small"
                      color={completedCount === totalCount ? 'success' : 'default'}
                      variant="outlined"
                      sx={chipSx}
                    />
                    <Chip
                      label={`完成率 ${completionRateForCard}%`}
                      size="small"
                      color={
                        completionRateForCard === 100
                          ? 'success'
                          : completionRateForCard > 0
                            ? 'primary'
                            : 'default'
                      }
                      variant={completionRateForCard === 100 ? 'filled' : 'outlined'}
                      sx={chipSx}
                    />
                  </Stack>
                </CardContent>
                <CardActions sx={cardActionsSx}>
                  <Button
                    size="small"
                    fullWidth={!isTeacher}
                    sx={{
                      flex: isTeacher ? 1 : undefined,
                      minWidth: isTeacher ? 0 : undefined,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      color: '#000000',
                      borderRadius: 2,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        color: '#000000',
                      },
                    }}
                    onClick={() => onViewQuestions(notebookIdForCard, notebookTitle)}
                  >
                    查看题目
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
